import {
  UserProfile,
  CallRecord,
  AccountStatus,
  AttackType,
  AnalyticsFilters,
  FilteredAnalytics,
  AuditLog,
  AuditAction,
} from '../types';
import { subDays, format, startOfDay, isWithinInterval } from 'date-fns';

// New type definitions for enhanced placeholder data
interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  message: string;
  status: 'new' | 'read' | 'replied' | 'archived';
  submittedAt: Date;
  type: 'contact';
}

interface SupportTicket {
  id: string;
  userId: string;
  userEmail: string;
  issueType: string;
  message: string;
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  createdAt: Date;
  type: 'support';
}

interface Release {
  id: string;
  version: string;
  buildNumber: number;
  buildDate: Date;
  fileSize: number;
  fileName: string;
  downloadURL: string;
  releaseNotes: string;
  minAndroidVersion: string;
  packageName: string;
  isLatest: boolean;
  createdAt: Date;
  updatedAt: Date;
}

interface PlaceholderData {
  users: UserProfile[];
  callRecords: CallRecord[];
  contactSubmissions: ContactSubmission[];
  supportTickets: SupportTicket[];
  auditLogs: AuditLog[];
  releases: Release[];
  version: string;
}

class PlaceholderDataService {
  private readonly STORAGE_KEY = 'deepfake_placeholder_data_v2';
  private readonly VERSION = '2.0.0';
  private data: PlaceholderData | null = null;

  // Seeded random number generator for reproducible results
  private seed = 12345;
  private seededRandom(): number {
    this.seed = (this.seed * 9301 + 49297) % 233280;
    return this.seed / 233280;
  }

  // Data pools for realistic data generation
  private readonly FIRST_NAMES = [
    // Western (30)
    'James',
    'Emma',
    'Michael',
    'Sophia',
    'William',
    'Olivia',
    'David',
    'Ava',
    'Richard',
    'Isabella',
    'Joseph',
    'Mia',
    'Thomas',
    'Charlotte',
    'Charles',
    'Amelia',
    'Christopher',
    'Harper',
    'Daniel',
    'Evelyn',
    'Matthew',
    'Abigail',
    'Anthony',
    'Emily',
    'Mark',
    'Elizabeth',
    'Donald',
    'Sofia',
    'Steven',
    'Madison',
    // Asian (20)
    'Wei',
    'Li',
    'Yuki',
    'Sakura',
    'Raj',
    'Priya',
    'Jin',
    'Min',
    'Arjun',
    'Ananya',
    'Kenji',
    'Hana',
    'Kumar',
    'Aisha',
    'Liang',
    'Mei',
    'Hassan',
    'Fatima',
    'Tan',
    'Lin',
    // European (15)
    'Marco',
    'Sofia',
    'Hans',
    'Emma',
    'Pierre',
    'Marie',
    'Carlos',
    'Ana',
    'Ivan',
    'Natasha',
    'Ahmed',
    'Zainab',
    'Dimitri',
    'Elena',
    'Lars',
    // Other (10)
    'Jamal',
    'Amara',
    'Santiago',
    'Gabriela',
    'Kwame',
    'Nia',
    'Omar',
    'Layla',
    'Mateo',
    'Lucia',
  ];

  private readonly LAST_NAMES = [
    // Western (30)
    'Smith',
    'Johnson',
    'Williams',
    'Brown',
    'Jones',
    'Garcia',
    'Miller',
    'Davis',
    'Rodriguez',
    'Martinez',
    'Hernandez',
    'Lopez',
    'Gonzalez',
    'Wilson',
    'Anderson',
    'Thomas',
    'Taylor',
    'Moore',
    'Jackson',
    'Martin',
    'Lee',
    'Thompson',
    'White',
    'Harris',
    'Sanchez',
    'Clark',
    'Lewis',
    'Robinson',
    'Walker',
    'Young',
    // Asian (20)
    'Wang',
    'Li',
    'Zhang',
    'Chen',
    'Liu',
    'Yang',
    'Huang',
    'Zhao',
    'Kumar',
    'Singh',
    'Patel',
    'Sharma',
    'Tanaka',
    'Suzuki',
    'Sato',
    'Kim',
    'Park',
    'Choi',
    'Nguyen',
    'Tran',
    // European (15)
    'Mueller',
    'Schmidt',
    'Schneider',
    'Fischer',
    'Dubois',
    'Bernard',
    'Rossi',
    'Ferrari',
    'Romano',
    'Ivanov',
    'Petrov',
    'Kowalski',
    'Novak',
    'Silva',
    'Santos',
    // Other (10)
    'Okafor',
    'Mensah',
    'Mbeki',
    'Diallo',
    'Kone',
    'Nkrumah',
    'Al-Farsi',
    'Al-Hassan',
    'Rojas',
    'Morales',
  ];

  private readonly EMAIL_DOMAINS = [
    { domain: 'gmail.com', weight: 40 },
    { domain: 'yahoo.com', weight: 15 },
    { domain: 'outlook.com', weight: 15 },
    { domain: 'hotmail.com', weight: 10 },
    { domain: 'protonmail.com', weight: 5 },
    { domain: 'icloud.com', weight: 5 },
    { domain: 'company.com', weight: 3 },
    { domain: 'university.edu', weight: 3 },
    { domain: 'work.net', weight: 2 },
    { domain: 'mail.com', weight: 2 },
  ];

  private readonly PHONE_FORMATS = [
    { prefix: '+1', weight: 40, digitCount: 10 }, // USA/Canada
    { prefix: '+65', weight: 15, digitCount: 8 }, // Singapore
    { prefix: '+44', weight: 15, digitCount: 10 }, // UK
    { prefix: '+91', weight: 10, digitCount: 10 }, // India
    { prefix: '+61', weight: 8, digitCount: 9 }, // Australia
    { prefix: '+81', weight: 5, digitCount: 10 }, // Japan
    { prefix: '+49', weight: 4, digitCount: 10 }, // Germany
    { prefix: '+33', weight: 3, digitCount: 9 }, // France
  ];

  private readonly DEVICE_NAMES = [
    // Apple (40%)
    'iPhone 15 Pro',
    'iPhone 15',
    'iPhone 14 Pro',
    'iPhone 14',
    'iPhone 13 Pro',
    'iPhone 13',
    'iPhone 12 Pro',
    'iPhone 12',
    'iPhone 11',
    'iPhone SE',
    'iPad Pro',
    'iPad Air',
    // Samsung (30%)
    'Galaxy S24 Ultra',
    'Galaxy S24',
    'Galaxy S23',
    'Galaxy S22',
    'Galaxy A54',
    'Galaxy A34',
    'Galaxy Z Fold 5',
    'Galaxy Z Flip 5',
    // Google (10%)
    'Pixel 8 Pro',
    'Pixel 8',
    'Pixel 7 Pro',
    'Pixel 7',
    'Pixel 6a',
    // Other Android (15%)
    'OnePlus 11',
    'Xiaomi 13',
    'OPPO Find X6',
    'Vivo X90',
    'Realme GT3',
    'Nothing Phone 2',
    'Motorola Edge 40',
    // Generic (5%)
    'Android Device',
    'Unknown Device',
  ];

  private readonly ISSUE_TYPES = [
    { type: 'App Crashes', weight: 15 },
    { type: 'Audio Quality Issues', weight: 12 },
    { type: 'Detection Accuracy Concerns', weight: 10 },
    { type: 'Login/Authentication Problems', weight: 10 },
    { type: 'Call Recording Not Working', weight: 8 },
    { type: 'Sync Issues', weight: 8 },
    { type: 'Performance/Lag', weight: 7 },
    { type: 'Battery Drain', weight: 7 },
    { type: 'Permission Issues', weight: 6 },
    { type: 'Feature Not Working as Expected', weight: 6 },
    { type: 'Data Not Updating', weight: 5 },
    { type: 'Export Functionality', weight: 3 },
    { type: 'Notification Problems', weight: 2 },
    { type: 'UI/UX Issue', weight: 1 },
  ];

  /**
   * Initialize placeholder data (call this on app startup in dev mode)
   */
  initialize(): void {
    const stored = localStorage.getItem(this.STORAGE_KEY);

    if (stored) {
      try {
        const parsed: PlaceholderData = JSON.parse(stored);

        // Check version - if mismatch, regenerate
        if (parsed.version !== this.VERSION) {
          console.log(
            `Migrating placeholder data from ${parsed.version || '1.0.0'} to ${this.VERSION}...`
          );
          this.regenerateData();
          return;
        }

        // Convert date strings back to Date objects
        this.data = {
          ...parsed,
          users: parsed.users.map((u) => ({
            ...u,
            createdAt: new Date(u.createdAt),
            lastLoginAt: u.lastLoginAt ? new Date(u.lastLoginAt) : undefined,
          })),
          callRecords: parsed.callRecords.map((c) => ({
            ...c,
            timestamp: new Date(c.timestamp),
          })),
          contactSubmissions: (parsed.contactSubmissions || []).map((s) => ({
            ...s,
            submittedAt: new Date(s.submittedAt),
          })),
          supportTickets: (parsed.supportTickets || []).map((t) => ({
            ...t,
            createdAt: new Date(t.createdAt),
          })),
          auditLogs: (parsed.auditLogs || []).map((l) => ({
            ...l,
            timestamp: new Date(l.timestamp),
          })),
          releases: (parsed.releases || []).map((r) => ({
            ...r,
            buildDate: new Date(r.buildDate),
            createdAt: new Date(r.createdAt),
            updatedAt: new Date(r.updatedAt),
          })),
        };
        console.log('✅ Loaded placeholder data from localStorage');
        return;
      } catch (error) {
        console.warn('Failed to parse stored data, regenerating...', error);
      }
    }

    // Generate new data
    this.regenerateData();
  }

  /**
   * Regenerate all placeholder data (FYP 90-day timeline)
   * OPTIMIZED: Reduced volumes to fit localStorage quota (~5 MB limit)
   */
  regenerateData(): void {
    console.log('🔄 Generating new placeholder data (FYP 90-day timeline)...');
    this.seed = 12345; // Reset seed for reproducibility

    const users = this.generateUsers(80);
    const admins = users.filter((u) => u.role === 'admin');
    const callRecords = this.generateCallRecords(users, 8000);
    const contactSubmissions = this.generateContactSubmissions(50);
    const supportTickets = this.generateSupportTickets(users, 30);
    const auditLogs = this.generateAuditLogs(admins, users, 100);
    const releases = this.generateReleases(6);

    this.data = {
      users,
      callRecords,
      contactSubmissions,
      supportTickets,
      auditLogs,
      releases,
      version: this.VERSION,
    };

    // Save to localStorage
    const dataString = JSON.stringify(this.data);
    const sizeInMB = (dataString.length / (1024 * 1024)).toFixed(2);

    try {
      localStorage.setItem(this.STORAGE_KEY, dataString);
      console.log('✅ Generated and saved placeholder data');
      console.log(`   - Users: ${users.length}`);
      console.log(`   - Call Records: ${callRecords.length}`);
      console.log(`   - Contact Submissions: ${contactSubmissions.length}`);
      console.log(`   - Support Tickets: ${supportTickets.length}`);
      console.log(`   - Audit Logs: ${auditLogs.length}`);
      console.log(`   - Releases: ${releases.length}`);
      console.log(`   - Storage size: ${sizeInMB} MB`);
    } catch (e) {
      console.error('❌ Failed to save placeholder data to localStorage:', e);
      console.error(`   - Data size: ${sizeInMB} MB (exceeds quota)`);
      throw e;
    }
  }

  // ========== HELPER METHODS ==========

  /**
   * Weighted selection utility
   */
  private selectWeighted<T>(items: { item: T; weight: number }[]): T {
    const total = items.reduce((sum, i) => sum + i.weight, 0);
    const rand = this.seededRandom() * total;
    let cumulative = 0;
    for (const { item, weight } of items) {
      cumulative += weight;
      if (rand < cumulative) return item;
    }
    return items[items.length - 1].item;
  }

  /**
   * Weighted date generation (favors recent dates)
   */
  private generateWeightedDate(
    startDate: Date,
    endDate: Date,
    recentWeight: number = 1.5
  ): Date {
    const t = Math.pow(this.seededRandom(), 1 / recentWeight);
    const range = endDate.getTime() - startDate.getTime();
    return new Date(startDate.getTime() + t * range);
  }

  /**
   * Generate realistic name
   */
  private generateRealisticName(): {
    firstName: string;
    lastName: string;
    fullName: string;
  } {
    const firstName =
      this.FIRST_NAMES[
        Math.floor(this.seededRandom() * this.FIRST_NAMES.length)
      ];
    const lastName =
      this.LAST_NAMES[Math.floor(this.seededRandom() * this.LAST_NAMES.length)];
    return { firstName, lastName, fullName: `${firstName} ${lastName}` };
  }

  /**
   * Generate realistic email
   */
  private generateRealisticEmail(name: string): string {
    const domain = this.selectWeighted(
      this.EMAIL_DOMAINS.map((d) => ({ item: d.domain, weight: d.weight }))
    );
    const username = name
      .toLowerCase()
      .replace(/\s+/g, '.')
      .replace(/[^a-z.]/g, '');
    return `${username}@${domain}`;
  }

  /**
   * Generate phone number (70% have phone)
   */
  private generatePhoneNumber(): string | undefined {
    if (this.seededRandom() > 0.7) return undefined;

    const format = this.selectWeighted(
      this.PHONE_FORMATS.map((f) => ({ item: f, weight: f.weight }))
    );

    let number = '';
    for (let i = 0; i < format.digitCount; i++) {
      number += Math.floor(this.seededRandom() * 10);
    }

    // Format with space after 4 digits
    return `${format.prefix} ${number.slice(0, 4)} ${number.slice(4)}`;
  }

  /**
   * Generate device name
   */
  private generateDeviceName(): string {
    return this.DEVICE_NAMES[
      Math.floor(this.seededRandom() * this.DEVICE_NAMES.length)
    ];
  }

  /**
   * Generate IP address
   */
  private generateIPAddress(): string {
    const rand = this.seededRandom();
    if (rand < 0.4) {
      // Local network (40%)
      return `192.168.1.${Math.floor(this.seededRandom() * 255)}`;
    } else if (rand < 0.7) {
      // Corporate VPN (30%)
      return `10.0.0.${Math.floor(this.seededRandom() * 255)}`;
    } else if (rand < 0.9) {
      // Office network (20%)
      return `172.16.${Math.floor(this.seededRandom() * 16)}.${Math.floor(this.seededRandom() * 255)}`;
    } else {
      // Public IP (10%)
      return `${Math.floor(this.seededRandom() * 223 + 1)}.${Math.floor(this.seededRandom() * 255)}.${Math.floor(this.seededRandom() * 255)}.${Math.floor(this.seededRandom() * 255)}`;
    }
  }

  // ========== GENERATOR METHODS ==========

  /**
   * Generate placeholder users with realistic data (80 users)
   * OPTIMIZED: Reduced from 100 to 80 to fit localStorage quota
   */
  private generateUsers(count: number): UserProfile[] {
    const users: UserProfile[] = [];
    const statuses: AccountStatus[] = ['active', 'inactive', 'suspended'];
    const statusWeights = [68, 8, 4]; // Distribution for 80 users (85% active, 10% inactive, 5% suspended)

    let statusIndex = 0;
    let statusRemaining = statusWeights[0];

    for (let i = 0; i < count; i++) {
      // Determine status based on weights
      if (statusRemaining === 0) {
        statusIndex++;
        if (statusIndex < statuses.length) {
          statusRemaining = statusWeights[statusIndex];
        }
      }
      const accountStatus =
        statuses[Math.min(statusIndex, statuses.length - 1)];
      statusRemaining--;

      // Determine role (5 admins, 95 users)
      const role = i < 5 ? 'admin' : 'user';

      // Generate realistic name
      const { fullName } = this.generateRealisticName();

      // Add edge cases for special characters
      let displayName = fullName;
      if (i === 50) displayName = "O'Brien Johnson";
      if (i === 51) displayName = 'José García';
      if (i === 52) displayName = 'François-Marie Dubois-Lefebvre III';

      const email = this.generateRealisticEmail(displayName);
      const phoneNumber = this.generatePhoneNumber();

      const createdAt = subDays(
        new Date(),
        Math.floor(this.seededRandom() * 90)
      ); // Random within last 90 days

      const lastLoginAt =
        accountStatus === 'active' && this.seededRandom() > 0.3
          ? subDays(new Date(), Math.floor(this.seededRandom() * 30)) // Active users logged in recently
          : accountStatus === 'inactive'
            ? subDays(new Date(), Math.floor(this.seededRandom() * 60 + 30)) // Inactive users logged in 30-90 days ago
            : undefined; // Suspended users might not have logged in recently

      users.push({
        uid: `user_${String(i + 1).padStart(4, '0')}`,
        email,
        displayName,
        phoneNumber,
        role: role as 'user' | 'admin',
        accountStatus,
        isGuestMode: false,
        termsAccepted: true,
        createdAt,
        lastLoginAt,
      });
    }

    // Add edge cases: users with 0 calls will be handled in call generation
    return users;
  }

  /**
   * Generate placeholder call records with FIXED model distribution (8,000 calls over 90 days)
   * CRITICAL: Ensures each model gets exactly ~33% for proper filtering
   * OPTIMIZED: Reduced from 15,000 to 8,000 to fit localStorage quota
   */
  private generateCallRecords(
    users: UserProfile[],
    totalCalls: number
  ): CallRecord[] {
    const callRecords: CallRecord[] = [];
    const models = ['AASIST-L', 'AASIST', 'DistilHuBERT + MLP'];
    const attackTypes: AttackType[] = [
      'Voice Conversion',
      'Text-to-Speech',
      'Voice Cloning',
      'Synthetic Speech',
      'Replay Attack',
    ];
    const attackTypeWeights = [0.35, 0.25, 0.2, 0.15, 0.05];

    const deepfakeRate = 0.28; // 28% deepfake rate
    const now = new Date();

    // CRITICAL FIX: Distribute calls evenly across models
    const callsPerModel = Math.floor(totalCalls / 3);

    for (let i = 0; i < totalCalls; i++) {
      // CRITICAL: Determine model based on index to ensure even distribution
      let modelUsed: string;
      if (i < callsPerModel) {
        modelUsed = models[0]; // AASIST-L
      } else if (i < callsPerModel * 2) {
        modelUsed = models[1]; // AASIST
      } else {
        modelUsed = models[2]; // DistilHuBERT + MLP
      }

      // Random user
      const user = users[Math.floor(this.seededRandom() * users.length)];

      // Date distribution - weighted towards recent dates
      // Ensure today has 30-50 calls, last 7 days has 250-350, etc.
      let timestamp: Date;
      const rand = this.seededRandom();

      if (rand < 0.05) {
        // 5% of calls today (30-50 calls out of 8,000)
        timestamp = new Date(now);
        timestamp.setHours(
          Math.floor(this.seededRandom() * 24),
          Math.floor(this.seededRandom() * 60),
          0,
          0
        );
      } else if (rand < 0.08) {
        // 3% yesterday (additional ~240 calls for last 7 days)
        timestamp = subDays(now, 1);
        timestamp.setHours(
          Math.floor(this.seededRandom() * 24),
          Math.floor(this.seededRandom() * 60),
          0,
          0
        );
      } else if (rand < 0.35) {
        // 27% in last 7 days (total ~35% = ~280 calls for last 7 days)
        timestamp = subDays(now, Math.floor(this.seededRandom() * 7));
        timestamp.setHours(
          Math.floor(this.seededRandom() * 24),
          Math.floor(this.seededRandom() * 60),
          0,
          0
        );
      } else {
        // Rest distributed over 90 days with slight weighting towards recent
        timestamp = this.generateWeightedDate(subDays(now, 90), now, 1.3);
      }

      // Determine if deepfake
      const isDeepfake = this.seededRandom() < deepfakeRate;

      // Select attack type
      let attackType: AttackType = 'Benign';
      if (isDeepfake) {
        const attackRand = this.seededRandom();
        let cumulative = 0;
        for (let j = 0; j < attackTypes.length; j++) {
          cumulative += attackTypeWeights[j];
          if (attackRand < cumulative) {
            attackType = attackTypes[j];
            break;
          }
        }
      }

      // Generate confidence score (higher for actual deepfakes)
      const deepfakeLikelihood = isDeepfake
        ? 0.7 + this.seededRandom() * 0.29 // 0.7 - 0.99 for deepfakes
        : 0.01 + this.seededRandom() * 0.39; // 0.01 - 0.4 for benign

      const deviceId = this.generateDeviceName();

      callRecords.push({
        id: `call_${String(i + 1).padStart(6, '0')}`,
        userId: user.uid,
        timestamp,
        duration: Math.floor(this.seededRandom() * 600 + 30), // 30-630 seconds
        deepfakeLikelihood,
        isDeepfake,
        modelUsed,
        attackType,
        deviceId,
      });
    }

    // Sort by timestamp descending (newest first)
    return callRecords.sort(
      (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
    );
  }

  /**
   * Generate contact submissions (80 records over 60 days)
   */
  private generateContactSubmissions(count: number): ContactSubmission[] {
    const submissions: ContactSubmission[] = [];
    const statuses: ('new' | 'read' | 'replied' | 'archived')[] = [
      'new',
      'read',
      'replied',
      'archived',
    ];
    const statusWeights = [0.17, 0.23, 0.4, 0.2];

    const subjects = [
      'General inquiry about app features',
      'Question about deepfake detection accuracy',
      'Partnership opportunity',
      'Media inquiry',
      'Feedback on app performance',
      'Feature request',
      'Privacy policy question',
      'Billing inquiry',
      'Enterprise solution interest',
      'Research collaboration',
    ];

    const messages = [
      'Hello, I would like to know more about your deepfake detection technology and how it works.',
      'Hi team, I noticed that the app is very accurate. Can you provide more technical details?',
      'Dear support, we are interested in using your solution for our organization. Could you provide pricing information?',
      'I have a question regarding the privacy policy and how user data is handled.',
      'Could you please provide information about the different ML models you use?',
      'I would like to suggest a new feature for the app that would be very useful.',
      'We are considering using your solution for our call center. What enterprise options are available?',
      'The app has been very helpful in identifying suspicious calls. Thank you!',
      'I am a researcher studying deepfake detection. Would you be interested in collaboration?',
      'How accurate is your deepfake detection compared to other solutions in the market?',
    ];

    const now = new Date();

    for (let i = 0; i < count; i++) {
      const { fullName } = this.generateRealisticName();
      const email = this.generateRealisticEmail(fullName);
      const status = this.selectWeighted(
        statuses.map((s, idx) => ({
          item: s,
          weight: statusWeights[idx] * 100,
        }))
      );

      const submittedAt = this.generateWeightedDate(subDays(now, 60), now, 1.5);

      const subject =
        subjects[Math.floor(this.seededRandom() * subjects.length)];
      let message = messages[Math.floor(this.seededRandom() * messages.length)];

      // Add edge case: very long message
      if (i === 0) {
        message = message + ' ' + message.repeat(10); // >1000 chars
      }

      submissions.push({
        id: `contact_${String(i + 1).padStart(4, '0')}`,
        name: fullName,
        email,
        message: `${subject}\n\n${message}`,
        status,
        submittedAt,
        type: 'contact',
      });
    }

    return submissions.sort(
      (a, b) => b.submittedAt.getTime() - a.submittedAt.getTime()
    );
  }

  /**
   * Generate support tickets (50 records over 60 days)
   */
  private generateSupportTickets(
    users: UserProfile[],
    count: number
  ): SupportTicket[] {
    const tickets: SupportTicket[] = [];
    const statuses: ('open' | 'in-progress' | 'resolved' | 'closed')[] = [
      'open',
      'in-progress',
      'resolved',
      'closed',
    ];
    const statusWeights = [0.2, 0.25, 0.35, 0.2];

    const messages = [
      'The app crashes when I try to export call history. Please help.',
      'I am experiencing issues with audio quality during calls. The detection seems to work but audio is poor.',
      'The detection results seem incorrect for some calls. Can you investigate?',
      'I cannot log in using my phone number. The verification code never arrives.',
      'My call history is not syncing to the cloud. I have enabled sync in settings.',
      'The app is very slow on my device. It takes forever to load the call list.',
      'Battery drains quickly while using the app for call monitoring.',
      'I need help with app permissions. The app is requesting camera access which seems unnecessary.',
      'Some features are not working as expected. The alert notifications do not appear.',
      'Data is not updating in real-time. I have to refresh manually.',
    ];

    const now = new Date();

    for (let i = 0; i < count; i++) {
      // Select a random user (70% regular users, 20% users with multiple tickets, 10% suspended/inactive)
      let user: UserProfile;
      if (this.seededRandom() < 0.1) {
        // Suspended/inactive users
        user = users.find((u) => u.accountStatus !== 'active') || users[0];
      } else {
        user = users[Math.floor(this.seededRandom() * users.length)];
      }

      const issueType = this.selectWeighted(
        this.ISSUE_TYPES.map((i) => ({ item: i.type, weight: i.weight }))
      );

      const status = this.selectWeighted(
        statuses.map((s, idx) => ({
          item: s,
          weight: statusWeights[idx] * 100,
        }))
      );

      const createdAt = this.generateWeightedDate(subDays(now, 60), now, 1.5);
      const message =
        messages[Math.floor(this.seededRandom() * messages.length)];

      tickets.push({
        id: `ticket_${String(i + 1).padStart(4, '0')}`,
        userId: user.uid,
        userEmail: user.email,
        issueType,
        message,
        status,
        createdAt,
        type: 'support',
      });
    }

    return tickets.sort(
      (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
    );
  }

  /**
   * Generate audit logs (150 records over 30 days)
   */
  private generateAuditLogs(
    admins: UserProfile[],
    users: UserProfile[],
    count: number
  ): AuditLog[] {
    const logs: AuditLog[] = [];
    const actions: AuditAction[] = [
      'view_user',
      'update_user',
      'update_account_status',
      'password_reset',
      'update_settings',
      'export_data',
      'delete_user',
    ];
    const actionWeights = [0.4, 0.2, 0.17, 0.1, 0.08, 0.03, 0.02];

    const now = new Date();

    for (let i = 0; i < count; i++) {
      // Select admin (weighted distribution)
      const adminRand = this.seededRandom();
      let admin: UserProfile;
      if (adminRand < 0.45)
        admin = admins[0]; // Admin 1: 45%
      else if (adminRand < 0.75)
        admin = admins[1] || admins[0]; // Admin 2: 30%
      else if (adminRand < 0.9)
        admin = admins[2] || admins[0]; // Admin 3: 15%
      else if (adminRand < 0.97)
        admin = admins[3] || admins[0]; // Admin 4: 7%
      else admin = admins[4] || admins[0]; // Admin 5: 3%

      const action = this.selectWeighted(
        actions.map((a, idx) => ({ item: a, weight: actionWeights[idx] * 100 }))
      );

      const timestamp = this.generateWeightedDate(subDays(now, 30), now, 2.0); // Heavy weighting towards recent

      // Select target user
      const targetUser = users[Math.floor(this.seededRandom() * users.length)];

      // Generate action-specific details
      let details: Record<string, any> = {};
      switch (action) {
        case 'view_user':
          details = {
            userId: targetUser.uid,
            reason: ['user_inquiry', 'routine_check', 'support_ticket'][
              Math.floor(this.seededRandom() * 3)
            ],
          };
          break;
        case 'update_user':
          details = {
            userId: targetUser.uid,
            changes: { displayName: targetUser.displayName },
          };
          break;
        case 'update_account_status':
          details = {
            userId: targetUser.uid,
            oldStatus: 'active',
            newStatus: targetUser.accountStatus,
            reason: 'Admin decision',
          };
          break;
        case 'password_reset':
          details = {
            userId: targetUser.uid,
            method: ['admin_reset', 'user_request'][
              Math.floor(this.seededRandom() * 2)
            ],
          };
          break;
        case 'update_settings':
          details = {
            settingKey: 'alertThreshold',
            oldValue: 70,
            newValue: 80,
          };
          break;
        case 'export_data':
          details = {
            dataType: ['users', 'calls', 'analytics'][
              Math.floor(this.seededRandom() * 3)
            ],
            format: 'csv',
          };
          break;
        case 'delete_user':
          details = {
            userId: targetUser.uid,
            reason: ['user_request', 'policy_violation', 'duplicate'][
              Math.floor(this.seededRandom() * 3)
            ],
          };
          break;
      }

      logs.push({
        id: `audit_${String(i + 1).padStart(4, '0')}`,
        adminId: admin.uid,
        adminEmail: admin.email,
        action,
        targetUserId: targetUser.uid,
        targetUserEmail: targetUser.email,
        details,
        timestamp,
        ipAddress: this.generateIPAddress(),
      });
    }

    return logs.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }

  /**
   * Generate app releases (8 releases over 90 days)
   */
  private generateReleases(count: number): Release[] {
    const releases: Release[] = [];
    const versions = [
      '1.0.0',
      '1.0.1',
      '1.1.0',
      '1.2.0',
      '2.0.0',
      '2.1.0',
      '2.2.0',
      '2.3.0',
    ];
    const buildNumbers = [100, 110, 120, 130, 200, 210, 220, 230];

    const features = [
      'Added support for DistilHuBERT + MLP model',
      'Introduced real-time call monitoring',
      'Enhanced detection accuracy by 5%',
      'New admin dashboard with analytics',
      'Offline detection support',
      'Multi-language support',
      'Improved battery efficiency',
      'Dark mode support',
    ];

    const improvements = [
      'Updated UI with Material Design 3',
      'Improved call history search',
      'Enhanced export functionality',
      'Better error messages',
      'Faster model loading time',
      'Improved notification system',
    ];

    const bugFixes = [
      'Fixed crash on Android 14',
      'Resolved audio quality issues',
      'Fixed sync problems',
      'Corrected notification timing',
      'Fixed memory leak in call recorder',
      'Resolved login authentication bug',
    ];

    const now = new Date();

    for (let i = 0; i < count; i++) {
      const version = versions[i];
      const buildNumber = buildNumbers[i];
      const buildDate = subDays(now, (count - i - 1) * 11); // Releases every ~11 days

      const isMajor = version.endsWith('.0.0');
      const numFeatures = isMajor ? 3 : 2;
      const numImprovements = isMajor ? 2 : 1;
      const numBugFixes = isMajor ? 3 : 2;

      const releaseFeatures = [];
      for (let j = 0; j < numFeatures; j++) {
        releaseFeatures.push(
          features[Math.floor(this.seededRandom() * features.length)]
        );
      }

      const releaseImprovements = [];
      for (let j = 0; j < numImprovements; j++) {
        releaseImprovements.push(
          improvements[Math.floor(this.seededRandom() * improvements.length)]
        );
      }

      const releaseBugFixes = [];
      for (let j = 0; j < numBugFixes; j++) {
        releaseBugFixes.push(
          bugFixes[Math.floor(this.seededRandom() * bugFixes.length)]
        );
      }

      const releaseNotes = `
        <h3>What's New in v${version}</h3>
        <ul>
          ${releaseFeatures.map((f) => `<li><strong>${f}</strong></li>`).join('\n          ')}
        </ul>
        <h4>Improvements</h4>
        <ul>
          ${releaseImprovements.map((imp) => `<li>${imp}</li>`).join('\n          ')}
        </ul>
        <h4>Bug Fixes</h4>
        <ul>
          ${releaseBugFixes.map((fix) => `<li>${fix}</li>`).join('\n          ')}
        </ul>
      `.trim();

      const fileSize = Math.floor(55 + this.seededRandom() * 90) * 1024 * 1024; // 55-145 MB in bytes

      releases.push({
        id: `release_${String(i + 1).padStart(2, '0')}`,
        version,
        buildNumber,
        buildDate,
        fileSize,
        fileName: `deepfake-detection-v${version}.apk`,
        downloadURL: `https://firebasestorage.googleapis.com/v0/b/deepfake-app-66e44.appspot.com/o/releases%2Fdeepfake-detection-v${version}.apk?alt=media`,
        releaseNotes,
        minAndroidVersion: '9.0',
        packageName: 'com.fyp.deepfakedetection',
        isLatest: i === count - 1, // Only the latest release
        createdAt: buildDate,
        updatedAt: buildDate,
      });
    }

    return releases.sort(
      (a, b) => b.buildDate.getTime() - a.buildDate.getTime()
    );
  }

  // ========== PUBLIC GETTERS ==========

  getUsers(): UserProfile[] {
    if (!this.data) {
      this.initialize();
    }
    return this.data?.users || [];
  }

  getCallRecords(): CallRecord[] {
    if (!this.data) {
      this.initialize();
    }
    return this.data?.callRecords || [];
  }

  getContactSubmissions(): ContactSubmission[] {
    if (!this.data) {
      this.initialize();
    }
    return this.data?.contactSubmissions || [];
  }

  getSupportTickets(): SupportTicket[] {
    if (!this.data) {
      this.initialize();
    }
    return this.data?.supportTickets || [];
  }

  getAuditLogs(): AuditLog[] {
    if (!this.data) {
      this.initialize();
    }
    return this.data?.auditLogs || [];
  }

  getReleases(): Release[] {
    if (!this.data) {
      this.initialize();
    }
    return this.data?.releases || [];
  }

  /**
   * Get filtered analytics based on filters
   */
  getFilteredAnalytics(filters: AnalyticsFilters): FilteredAnalytics {
    const callRecords = this.getCallRecords();

    // Filter by model
    let filteredCalls = callRecords;
    if (filters.model !== 'all') {
      filteredCalls = filteredCalls.filter(
        (call) => call.modelUsed === filters.model
      );
    }

    // Filter by date range
    filteredCalls = filteredCalls.filter((call) =>
      isWithinInterval(call.timestamp, {
        start: startOfDay(filters.dateRange.start),
        end: filters.dateRange.end,
      })
    );

    // Calculate metrics
    const totalCalls = filteredCalls.length;
    const deepfakeCalls = filteredCalls.filter((c) => c.isDeepfake).length;
    const legitimateCalls = totalCalls - deepfakeCalls;

    const avgConfidenceScore =
      totalCalls > 0
        ? filteredCalls.reduce((sum, c) => sum + c.deepfakeLikelihood, 0) /
          totalCalls
        : 0;

    // Model-specific accuracy (simulated)
    const modelAccuracies: Record<string, number> = {
      'AASIST-L': 0.89,
      AASIST: 0.92,
      'DistilHuBERT + MLP': 0.95,
    };
    const avgAccuracy =
      filters.model !== 'all'
        ? modelAccuracies[filters.model] || 0.9
        : Object.values(modelAccuracies).reduce((a, b) => a + b, 0) /
          Object.values(modelAccuracies).length;

    // Attack type distribution
    const attackTypeMap: Map<AttackType, number> = new Map();
    filteredCalls.forEach((call) => {
      const count = attackTypeMap.get(call.attackType) || 0;
      attackTypeMap.set(call.attackType, count + 1);
    });

    const attackTypeDistribution = Array.from(attackTypeMap.entries())
      .map(([type, count]) => ({ type, count }))
      .filter((item) => item.type !== 'Benign') // Exclude benign from attack type chart
      .sort((a, b) => b.count - a.count);

    // Monitoring trends (group by date)
    const trendsMap: Map<string, { calls: number; deepfakes: number }> =
      new Map();
    filteredCalls.forEach((call) => {
      const dateKey = format(call.timestamp, 'yyyy-MM-dd');
      const existing = trendsMap.get(dateKey) || { calls: 0, deepfakes: 0 };
      existing.calls++;
      if (call.isDeepfake) {
        existing.deepfakes++;
      }
      trendsMap.set(dateKey, existing);
    });

    const monitoringTrends = Array.from(trendsMap.entries())
      .map(([date, data]) => ({
        date,
        calls: data.calls,
        deepfakes: data.deepfakes,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return {
      totalCalls,
      legitimateCalls,
      deepfakeCalls,
      avgConfidenceScore,
      avgAccuracy,
      attackTypeDistribution,
      monitoringTrends,
    };
  }
}

export default new PlaceholderDataService();
